import { Injectable } from "@angular/core";
import { HttpService } from "../http.service";
import { ADMIN_API_ENDPOINTS } from "../../common/api-endpoints";
import { PageResultDto } from "@api/dto";
import { ExportOrderDto } from "@api/dto/order.dto";
import { FNB_ORDER_STATUS_TO_STRING, FnbOrderStatus } from "@api/dto/governance-admin";

const BASE = ADMIN_API_ENDPOINTS.FNB_ORDERS;

export interface ExportExcelResponseDto {
  success?: boolean;
  message?: string;
  /** If backend supports async export job tracking */
  jobId?: string;
}

@Injectable({ providedIn: "root" })
export class FnbOrderApiService {
  constructor(private readonly httpService: HttpService) {}

  /**
   * Update F&B order. PATCH /admin/v1/fnb-orders/{id}
   * Body: { orderStatus: string, deliveryMan?: string }
   */
  async updateOrder(
    id: string,
    body: { orderStatus: string; deliveryMan?: string }
  ): Promise<void> {
    await this.httpService.put<unknown>(`${BASE}/${id}`, body);
  }

  /**
   * Fetch F&B orders list with optional filters.
   * Supports server-side paging.
   * Sends: orderId, tenantId, orderStatus (string), dateFrom, dateTo, limit, offset.
   */
  async find(params: {
    orderId?: string;
    tenantId?: string;
    offlineTenantId?: string;
    mallId?: string;
    status?: number | null;
    orderStatus?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    rows: Array<{
      id: string;
      orderId: string;
      tenantId?: string;
      tenantName: string;
      productName: string;
      totalAmount: number;
      orderDate: string;
      deliveryDate: string;
      status: number;
    }>;
    count: number;
  }> {
    const query: Record<string, string | number> = {};
    if (params.orderId) query.orderId = params.orderId;
    if (params.mallId) query.mallId = params.mallId;
    const tid = params.offlineTenantId ?? params.tenantId;
    if (tid) query.tenantId = tid;
    const orderStatus = params.orderStatus ?? (params.status != null ? this.statusToOrderStatus(params.status) : undefined);
    if (orderStatus) query.orderStatus = orderStatus;
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.limit != null) query.limit = params.limit;
    if (params.offset != null) query.offset = params.offset;

    const result = await this.httpService.get<
      PageResultDto<{
        id: string;
        orderId: string;
        tenantId?: string;
        tenantName: string;
        productName: string;
        totalAmount: number;
        orderDate: string;
        deliveryDate: string;
        status: number;
      }>
    >(BASE, query);

    return {
      rows: result?.rows ?? [],
      count: result?.count ?? 0,
    };
  }

  private statusToOrderStatus(status: number): string {
    return (
      FNB_ORDER_STATUS_TO_STRING[status as keyof typeof FNB_ORDER_STATUS_TO_STRING] ??
      FNB_ORDER_STATUS_TO_STRING[FnbOrderStatus.New]
    );
  }

  /**
   * Fetch F&B order detail by id.
   */
  async findOne(id: string): Promise<FnbOrderDetailResponse> {
    return await this.httpService.get<FnbOrderDetailResponse>(`${BASE}/${id}`);
  }

  /**
   * Export F&B orders report (async by email).
   */
  async exportExcel(
    payload: ExportOrderDto & {
      orderStatus?: string;
      orderId?: string;
      tenantId?: string;
      mallId?: string;
    }
  ): Promise<ExportExcelResponseDto> {
    // Backend accepts `tenantId` directly for export filters.
    return await this.httpService.post<ExportExcelResponseDto>(`${BASE}/export-excel`, payload);
  }
}

/** PayWay check-status callback fields when present on stored payment JSON */
export interface FnbOrderPaymentCheckStatusResponse {
  datetime?: string;
  transaction_date?: string;
}

/** PayWay / checkout payload embedded on order detail when present */
export interface FnbOrderPaymentData {
  url?: string;
  hash?: string;
  email?: string;
  amount?: number;
  tranId?: string;
  tran_id?: string;
  lifetime?: number;
  cancelUrl?: string;
  returnUrl?: string;
  merchantId?: string;
  orderNumber?: string;
  /** Format YYYYMMDDHHmmss (e.g. 20260320163956) */
  requestTime?: string;
  returnParams?: string;
  paymentOption?: string;
  continueShoppingUrl?: string;
  checkStatusResponse?: FnbOrderPaymentCheckStatusResponse;
}

export interface FnbOrderDetailResponse {
  id: string;
  createdAt?: string;
  updatedAt?: string;
  offlineTenantId?: string;
  userOrderId?: string;
  orderNumber?: string;
  tenantName?: string | { en?: string; kh?: string };
  productName?: string;
  productNames?: string[];
  orderDetail?: Array<{
    name?: string | { en?: string; kh?: string };
    quantity?: number;
    price?: number;
  }>;
  status?: string | number;
  orderDate?: string;
  deliveryDate?: string;
  customerNote?: string;
  tenantNote?: string;
  orderStatus?: string;
  paymentStatus?: string;
  paymentMethodName?: string;
  products?: Array<{
    product_name?: string | { en?: string; kh?: string };
    quantity?: number;
    base_price?: number;
    unit_total_price?: number;
    customerNote?: string;
    customer_note?: string;
    selected_options?: Array<{
      group_name?: string | { en?: string; kh?: string };
      selected_items?: Array<{
        name?: string | { en?: string; kh?: string };
        extra_price?: number;
      }>;
    }>;
  }>;
  totalPrice?: number;
  confirmedAt?: string;
  completedAt?: string;
  rejectedAt?: string;
  cancelledAt?: string;
  /** Customer profile (detail API) */
  orderUser?: {
    id?: string;
    fullName?: string;
    phoneNumber?: string;
    email?: string | null;
  };
  /** Table / mall / floor (detail API) */
  mallTable?: {
    id?: string;
    code?: string;
    name?: string | { en?: string; kh?: string };
    mall?: { id?: string; name?: string | { en?: string; kh?: string } };
    floor?: { id?: string; name?: string | { en?: string; kh?: string } };
  };
  transactionID?: string;
  paymentDate?: string;
  /** Checkout / PayWay metadata (detail API) */
  paymentData?: FnbOrderPaymentData;
}
